import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { Modal } from "../components/Modal";
import { Field, Input, Textarea } from "../components/Field";
import {
  usePromoteUser,
  useDemoteUser,
  useGrantCredits,
  useBanUser,
  useUnbanUser,
  usePatchRateLimit,
  useCascadeRecover,
  useImpersonate,
  useDeleteAllSessions,
} from "../auth/adminMutations";
import type { components } from "../api/types";

type AdminUser = components["schemas"]["User"];
type ProductSummary = components["schemas"]["ProductSummary"];
type APIToken = components["schemas"]["APIToken"];

export function UserDetail() {
  const { uid = "" } = useParams();
  const { data: me } = useUser();

  const userQ = useQuery({
    queryKey: ["admin", "user", uid],
    queryFn: async (): Promise<AdminUser> => {
      const { data, error } = await apiClient.GET("/api/v1/admin/users/{uid}", {
        params: { path: { uid } },
      });
      if (error) throw error;
      return data as AdminUser;
    },
  });

  const productsQ = useQuery({
    queryKey: ["admin", "user", uid, "products"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/admin/users/{uid}/products",
        { params: { path: { uid } } },
      );
      if (error) throw error;
      return data as { products: ProductSummary[]; next_cursor?: string };
    },
  });

  if (!me) return null;
  const user = userQ.data;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-10">
        <Link to="/" className="font-mono text-[11px] text-ink-400 hover:text-ink-100">
          ← Users
        </Link>
        {userQ.isLoading && <p className="mt-6 text-sm text-ink-400">loading…</p>}
        {userQ.error && (
          <p className="mt-6 text-sm text-rose-300">
            Failed: {(userQ.error as Error).message}
          </p>
        )}
        {user && (
          <>
            <Profile user={user} />
            <Actions user={user} actorUID={me.id} />
            <Products
              uid={uid}
              products={productsQ.data?.products ?? []}
              loading={productsQ.isLoading}
            />
            <Sessions uid={uid} />
            <APITokens uid={uid} />
          </>
        )}
      </main>
    </div>
  );
}

function Profile({ user }: { user: AdminUser }) {
  return (
    <section className="mt-8 mb-10 animate-fade-in">
      <h1 className="text-display font-medium text-ink-50 tracking-tightest mb-2">
        {user.display_name}
      </h1>
      <p className="font-mono text-[11px] text-ink-400 mb-6">{user.id}</p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <DLRow label="email" value={user.email} />
        <DLRow label="plan" value={user.plan} />
        <DLRow label="credit_balance" value={String(user.credit_balance)} />
        <DLRow label="is_admin" value={user.is_admin ? "true" : "false"} />
        <DLRow label="created_at" value={user.created_at} />
        <DLRow label="last_login_at" value={user.last_login_at} />
        {user.banned_at && (
          <>
            <DLRow label="banned_at" value={user.banned_at} highlight="rose" />
            <DLRow
              label="ban_reason"
              value={user.ban_reason ?? ""}
              highlight="rose"
            />
          </>
        )}
        {(user as { deleted_at?: string }).deleted_at && (
          <DLRow
            label="deleted_at"
            value={(user as { deleted_at?: string }).deleted_at ?? ""}
            highlight="amber"
          />
        )}
        {user.rate_limit_override &&
          Object.keys(user.rate_limit_override).length > 0 && (
            <DLRow
              label="rate_limit_override"
              value={JSON.stringify(user.rate_limit_override)}
            />
          )}
      </dl>
    </section>
  );
}

function DLRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: "rose" | "amber";
}) {
  const valueColor =
    highlight === "rose"
      ? "text-rose-300"
      : highlight === "amber"
      ? "text-amber-300"
      : "text-ink-100";
  return (
    <>
      <dt className="font-mono text-[10px] uppercase tracking-widest text-ink-400">
        {label}
      </dt>
      <dd className={`${valueColor}`}>{value || "—"}</dd>
    </>
  );
}

function Actions({ user, actorUID }: { user: AdminUser; actorUID: string }) {
  const [open, setOpen] = useState<
    null | "credits" | "ban" | "rate" | "impersonate"
  >(null);
  const promote = usePromoteUser(user.id);
  const demote = useDemoteUser(user.id);
  const unban = useUnbanUser(user.id);
  const recover = useCascadeRecover(user.id);
  const isTombstoned = !!(user as { deleted_at?: string }).deleted_at;
  const isBanned = !!user.banned_at;
  const isSelf = user.id === actorUID;

  return (
    <section className="mb-10 border-t border-ink-800 pt-6">
      <h2 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
        Actions
      </h2>
      <div className="flex flex-wrap gap-2">
        {!user.is_admin && (
          <ActionBtn
            onClick={() => promote.mutate()}
            disabled={promote.isPending}
          >
            Promote to admin
          </ActionBtn>
        )}
        {user.is_admin && !isSelf && (
          <ActionBtn
            onClick={() => demote.mutate()}
            disabled={demote.isPending}
          >
            Demote
          </ActionBtn>
        )}
        <ActionBtn onClick={() => setOpen("credits")}>Grant credits</ActionBtn>
        {!isBanned ? (
          <ActionBtn onClick={() => setOpen("ban")} variant="danger">
            Ban
          </ActionBtn>
        ) : (
          <ActionBtn onClick={() => unban.mutate()} disabled={unban.isPending}>
            Unban
          </ActionBtn>
        )}
        <ActionBtn onClick={() => setOpen("rate")}>Rate-limit override</ActionBtn>
        {isTombstoned && (
          <ActionBtn
            onClick={() => recover.mutate()}
            disabled={recover.isPending}
            variant="amber"
          >
            Cascade-delete recover
          </ActionBtn>
        )}
        {!isSelf && !isBanned && !isTombstoned && (
          <ActionBtn onClick={() => setOpen("impersonate")}>
            Impersonate
          </ActionBtn>
        )}
      </div>
      {(promote.error || demote.error || unban.error || recover.error) && (
        <p className="mt-2 text-xs text-rose-300">
          {String(
            (promote.error ?? demote.error ?? unban.error ?? recover.error)
              ?.message ?? "",
          )}
        </p>
      )}
      {recover.data && (
        <p className="mt-2 text-xs text-ink-400">
          Recovered: {recover.data.products_deleted} products,{" "}
          {recover.data.runs_deleted} runs.
        </p>
      )}

      <GrantCreditsModal
        uid={user.id}
        open={open === "credits"}
        onClose={() => setOpen(null)}
      />
      <BanModal
        uid={user.id}
        open={open === "ban"}
        onClose={() => setOpen(null)}
      />
      <RateLimitModal
        uid={user.id}
        current={user.rate_limit_override ?? {}}
        open={open === "rate"}
        onClose={() => setOpen(null)}
      />
      <ImpersonateModal
        uid={user.id}
        open={open === "impersonate"}
        onClose={() => setOpen(null)}
      />
    </section>
  );
}

function ActionBtn({
  children,
  onClick,
  disabled,
  variant,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: "danger" | "amber";
}) {
  const cls =
    variant === "danger"
      ? "bg-rose-950 border-rose-900 text-rose-200 hover:border-rose-700"
      : variant === "amber"
      ? "bg-amber-950 border-amber-900 text-amber-200 hover:border-amber-700"
      : "bg-ink-900 border-ink-700 text-ink-100 hover:border-accent";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-xs px-3 py-1.5 rounded border transition-colors disabled:opacity-50 ${cls}`}
    >
      {children}
    </button>
  );
}

function Products({
  uid,
  products,
  loading,
}: {
  uid: string;
  products: ProductSummary[];
  loading: boolean;
}) {
  return (
    <section className="border-t border-ink-800 pt-6">
      <h2 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
        Products ({products.length})
      </h2>
      {loading && <p className="text-sm text-ink-400">loading…</p>}
      <ul className="space-y-2">
        {products.map((p) => (
          <li key={p.id}>
            <Link
              to={`/users/${uid}/products/${p.id}`}
              className="block text-sm text-ink-100 hover:text-accent transition-colors"
            >
              <span className="font-mono text-[11px] text-ink-400 mr-3">
                {p.id.slice(0, 10)}…
              </span>
              {p.title || p.idea}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Modals ───────────────────────────────────────────────────────────────────

function GrantCreditsModal({
  uid,
  open,
  onClose,
}: {
  uid: string;
  open: boolean;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("");
  const grant = useGrantCredits(uid);
  return (
    <Modal open={open} onClose={onClose} title="Grant credits">
      <Field label="Amount">
        <Input
          type="number"
          min={1}
          max={10000}
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
        />
      </Field>
      <Field label="Reason (optional)" hint="≤500 chars">
        <Textarea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      {grant.error && (
        <p className="text-xs text-rose-300 mb-2">
          {(grant.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded border border-ink-700 text-ink-300"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            await grant.mutateAsync({ amount, reason });
            onClose();
          }}
          disabled={grant.isPending}
          className="text-xs px-3 py-1.5 rounded bg-accent text-ink-950 disabled:opacity-50"
        >
          Grant
        </button>
      </div>
    </Modal>
  );
}

function BanModal({
  uid,
  open,
  onClose,
}: {
  uid: string;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const ban = useBanUser(uid);
  return (
    <Modal open={open} onClose={onClose} title="Ban user">
      <p className="text-sm text-ink-300 mb-3">
        This terminates all active sessions and 403s every authenticated
        request from this user until you Unban.
      </p>
      <Field label="Reason" hint="1..500 chars; visible in audit log">
        <Textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      {ban.error && (
        <p className="text-xs text-rose-300 mb-2">
          {(ban.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded border border-ink-700 text-ink-300"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            await ban.mutateAsync({ reason });
            onClose();
          }}
          disabled={ban.isPending || !reason.trim()}
          className="text-xs px-3 py-1.5 rounded bg-rose-700 text-ink-50 disabled:opacity-50"
        >
          Ban
        </button>
      </div>
    </Modal>
  );
}

function RateLimitModal({
  uid,
  current,
  open,
  onClose,
}: {
  uid: string;
  current: { [k: string]: number };
  open: boolean;
  onClose: () => void;
}) {
  const [runs, setRuns] = useState<string>(
    current.runs ? String(current.runs) : "",
  );
  const [undos, setUndos] = useState<string>(
    current.undos ? String(current.undos) : "",
  );
  const patch = usePatchRateLimit(uid);
  return (
    <Modal open={open} onClose={onClose} title="Rate-limit override">
      <p className="text-sm text-ink-300 mb-3">
        Empty = use env default. 0 = explicitly delete the override.
      </p>
      <Field label="runs / hour">
        <Input
          type="number"
          min={0}
          value={runs}
          onChange={(e) => setRuns(e.target.value)}
        />
      </Field>
      <Field label="undos / hour">
        <Input
          type="number"
          min={0}
          value={undos}
          onChange={(e) => setUndos(e.target.value)}
        />
      </Field>
      {patch.error && (
        <p className="text-xs text-rose-300 mb-2">
          {(patch.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded border border-ink-700 text-ink-300"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            const body: { runs?: number; undos?: number } = {};
            if (runs !== "") body.runs = Number(runs);
            if (undos !== "") body.undos = Number(undos);
            await patch.mutateAsync(body);
            onClose();
          }}
          disabled={patch.isPending}
          className="text-xs px-3 py-1.5 rounded bg-accent text-ink-950 disabled:opacity-50"
        >
          Apply
        </button>
      </div>
    </Modal>
  );
}

// USER_APP_URL is opened in a new tab on successful impersonation so
// the operator immediately lands on the page that renders the
// banner. Configurable via VITE_USER_APP_URL for staging / dev;
// defaults to https://app.eminity.com.
const USER_APP_URL =
  import.meta.env.VITE_USER_APP_URL ?? "https://app.eminity.com";

function ImpersonateModal({
  uid,
  open,
  onClose,
}: {
  uid: string;
  open: boolean;
  onClose: () => void;
}) {
  const [ttlMin, setTTLMin] = useState(30);
  const imp = useImpersonate(uid);
  return (
    <Modal open={open} onClose={onClose} title="Impersonate user">
      <p className="text-sm text-ink-300 mb-3">
        Mints a separate <code>pmt_session_admin</code> cookie. After
        you start, the user app opens in a new tab so you can see
        what the user sees. The sticky banner there will show
        "Impersonating &lt;email&gt;". Audit-logged.
      </p>
      <Field label="TTL (minutes)" hint="1..120">
        <Input
          type="number"
          min={1}
          max={120}
          value={ttlMin}
          onChange={(e) => setTTLMin(Number(e.target.value))}
        />
      </Field>
      {imp.error && (
        <p className="text-xs text-rose-300 mb-2">
          {(imp.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded border border-ink-700 text-ink-300"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            const result = await imp.mutateAsync({ ttl_seconds: ttlMin * 60 });
            // Open the user app in a new tab so the operator
            // immediately lands on the banner-rendering surface.
            // noopener+noreferrer because the user app is a
            // separate origin (different cookie scope, different
            // window.open context).
            if (result) {
              window.open(USER_APP_URL, "_blank", "noopener,noreferrer");
            }
            onClose();
          }}
          disabled={imp.isPending}
          className="text-xs px-3 py-1.5 rounded bg-accent text-ink-950 disabled:opacity-50"
        >
          Start impersonation
        </button>
      </div>
    </Modal>
  );
}

// ── Sessions section ─────────────────────────────────────────────────────────

function Sessions({ uid }: { uid: string }) {
  const q = useQuery({
    queryKey: ["admin", "user", uid, "sessions"],
    queryFn: async (): Promise<{ count: number }> => {
      const { data, error } = await apiClient.GET(
        "/api/v1/admin/users/{uid}/sessions",
        { params: { path: { uid } } },
      );
      if (error) throw error;
      return data as { count: number };
    },
  });
  const purge = useDeleteAllSessions(uid);

  return (
    <section className="border-t border-ink-800 pt-6 mb-10">
      <h2 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
        Sessions
      </h2>
      <div className="flex items-center gap-4">
        <p className="text-sm text-ink-300">
          {q.isLoading
            ? "loading…"
            : q.error
            ? `Failed: ${(q.error as Error).message}`
            : `${q.data?.count ?? 0} active`}
        </p>
        <button
          type="button"
          onClick={() => purge.mutate()}
          disabled={purge.isPending || (q.data?.count ?? 0) === 0}
          className="text-xs px-3 py-1.5 rounded border border-rose-900 bg-rose-950 text-rose-200 hover:border-rose-700 transition-colors disabled:opacity-40"
        >
          Sign out all sessions
        </button>
      </div>
      {purge.error && (
        <p className="mt-2 text-xs text-rose-300">
          {(purge.error as Error).message}
        </p>
      )}
      {purge.data && (
        <p className="mt-2 text-xs text-ink-400">
          Terminated {purge.data.sessions_terminated} session
          {purge.data.sessions_terminated === 1 ? "" : "s"}.
        </p>
      )}
    </section>
  );
}

// ── API tokens section ───────────────────────────────────────────────────────

function APITokens({ uid }: { uid: string }) {
  const q = useQuery({
    queryKey: ["admin", "user", uid, "api-tokens"],
    queryFn: async (): Promise<{ tokens: APIToken[] }> => {
      const { data, error } = await apiClient.GET(
        "/api/v1/admin/users/{uid}/api-tokens",
        { params: { path: { uid } } },
      );
      if (error) throw error;
      return data as { tokens: APIToken[] };
    },
  });
  const tokens = q.data?.tokens ?? [];
  return (
    <section className="border-t border-ink-800 pt-6">
      <h2 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
        API tokens ({q.isLoading ? "…" : tokens.length})
      </h2>
      {q.error && (
        <p className="text-sm text-rose-300">
          Failed: {(q.error as Error).message}
        </p>
      )}
      {tokens.length === 0 && !q.isLoading && (
        <p className="text-sm text-ink-400">None.</p>
      )}
      <ul className="space-y-1.5 text-sm">
        {tokens.map((t) => (
          <li
            key={t.token_id}
            className="font-mono text-[11px] text-ink-300"
          >
            <span className="text-ink-400 mr-3">{t.token_id?.slice(0, 12)}…</span>
            <span className="text-ink-100">{t.name}</span>
            <span className="ml-3 text-ink-400">
              created {t.created_at?.slice(0, 10)}
            </span>
            {t.last_used_at && (
              <span className="ml-3 text-ink-400">
                · last used {t.last_used_at?.slice(0, 10)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
